import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Modal,
  ActionBar,
  ButtonGroup,
  Icon,
  Loader,
  Input,
} from 'shared-components';
import { post } from 'shared-components/src/utils/http';
import { isExemptBin } from 'shared-components/src/utils/binExemption';
import { digitsOnly } from '../../../../utils/cardFormatting';
import { CONTACT_COUNTRY, contactCardErrors, formatPostalCode } from '../../../../utils/contactCard';
import {
  CardNumberInput,
  PostalCodeInput,
  ProvinceSelect,
  CountrySelect,
  ContactFieldError,
} from '../ContactFields';
import useFieldErrors from '../ContactFields/useFieldErrors';
import { StyledLabel, ModalFormContainer, InputRow, InputItem, CollapsibleSection, InfoRow } from './styles';

import SubmitButton from '../../../../components/SubmitButton';
import CancelButton from '../../../../components/CancelButton';
import Alert from '../../../../components/Alert';

const CreateExternalDirectsendAccountV2Modal = ({
  toggleModal,
  setSuccess,
  setExternalAccounts,
  page,
}) => {
  const intl = useIntl();
  const contactExternalAccountName = useRef(null);

  const contactInputFullName = useRef(null);
  const contactInputCardNumber = useRef(null);
  const contactInputExpiry = useRef(null);
  const contactInputCVV = useRef(null);
  const contactInputAddressLineOne = useRef(null);
  const contactInputAddressLineTwo = useRef(null);
  const contactInputCity = useRef(null);
  const contactInputPostalCode = useRef(null);

  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isBinExempt, setIsBinExempt] = useState(false);
  const [cardNumberLength, setCardNumberLength] = useState(0);
  const [province, setProvince] = useState('');

  const direction = page === 'directsend-push' ? 'push' : 'pull';

  const { fieldErrors, fieldChanged, validateField, validateAll, clearField, invalid } = useFieldErrors(() =>
    contactCardErrors({
      name: contactExternalAccountName.current?.value,
      fullName: contactInputFullName.current?.value,
      cardNumber: contactInputCardNumber.current?.value,
      expiry: contactInputExpiry.current?.value,
      cvv: contactInputCVV.current?.value,
      addressLineOne: contactInputAddressLineOne.current?.value,
      city: contactInputCity.current?.value,
      state: province,
      postalCode: contactInputPostalCode.current?.value,
    })
  );

  const handleCardNumberChange = e => {
    const cardNumber = e.target.value.replace(/\s/g, '');
    const newIsBinExempt = isExemptBin(cardNumber);

    // Reset CVV and expiry fields when BIN exemption status changes
    if (newIsBinExempt !== isBinExempt) {
      if (contactInputExpiry.current) contactInputExpiry.current.value = '';
      if (contactInputCVV.current) contactInputCVV.current.value = '';
    }

    setCardNumberLength(cardNumber.length);
    setIsBinExempt(newIsBinExempt);
  };

  const addContact = async () => {
    const name = contactExternalAccountName.current.value;
    const fullName = contactInputFullName.current.value;
    const cardNumber = contactInputCardNumber.current.value;
    const expiryValue = contactInputExpiry.current.value;
    const cvv = contactInputCVV.current.value;
    const addressLineOne = contactInputAddressLineOne.current.value;
    const addressLineTwo = contactInputAddressLineTwo.current.value;
    const city = contactInputCity.current.value;
    const postalCode = contactInputPostalCode.current.value;

    try {
      const cardPayload = {
        name: fullName,
        number: digitsOnly(cardNumber),
        address_line1: addressLineOne,
        address_line2: addressLineTwo,
        address_city: city,
        address_state: province,
        address_country: CONTACT_COUNTRY,
        address_postal_code: formatPostalCode(postalCode),
      };

      if (expiryValue) {
        const [expiryMonth, expiryYear] = expiryValue.split('/');
        cardPayload.expiry_month = expiryMonth;
        cardPayload.expiry_year = expiryYear;
      }

      if (cvv) {
        cardPayload.cvv = cvv;
      }

      const externalAccountPayload = {
        name,
        card: cardPayload,
        type:
          page === 'directsend-push'
            ? 'personal_funding_account'
            : 'personal_source_account',
      };

      const { data } = await post(
        '/api/v2/cardholder_portal/cardholder/external_accounts',
        externalAccountPayload
      );

      if (!data || !data.id) {
        throw new Error('Failed to create external account');
      }

      return data;
    } catch (error) {
      throw new Error(error.message || 'Failed to create external account');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateAll()) return;

    setLoading(true);
    setAlert(null);

    try {
      const newContact = await addContact();
      setExternalAccounts(externalAccounts => [
        ...externalAccounts,
        newContact,
      ]);

      setSuccess({
        type: 'success',
        message: intl.messages[`${page}-success-create-alert`],
        messageFor: 'success-create',
        component: page,
        page,
      });

      setLoading(false);
      toggleModal(false);
    } catch (error) {
      setAlert({ type: 'danger', message: error.message });
      setLoading(false);
      setCardNumberLength(0);
    }
  };

  return (
    <Modal>
      <ModalFormContainer>
        {alert && (
          <Alert
            alert={alert.message}
            alertFor={alert.messageFor}
            page={page}
            type={alert.type}
          />
        )}
        <h2>{intl.messages[`${page}-create-contact-heading`]}</h2>
        {loading ? (
          <Loader />
        ) : (
          // noValidate: every field is checked as it is left and on submit, with its message
          // shown under it, instead of the browser's own bubble.
          <form noValidate onChange={fieldChanged} onSubmit={e => handleSubmit(e)}>
            <InputRow>
              <InputItem>
                <StyledLabel htmlFor="contact-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-name-input-label`]}
                </StyledLabel>
                <Input
                  id="contact-name-input"
                  type="text"
                  ref={contactExternalAccountName}
                  className={invalid('name')}
                  onBlur={() => validateField('name')}
                  required
                />
                <ContactFieldError error={fieldErrors.name} />
              </InputItem>
              <InputItem>
                <StyledLabel htmlFor="contact-full-cardholder-name-input">
                  <span>*</span>{' '}
                  {
                    intl.messages[
                      `${page}-contact-full-cardholder-name-input-label`
                    ]
                  }
                </StyledLabel>
                <Input
                  id="contact-full-cardholder-name-input"
                  type="text"
                  ref={contactInputFullName}
                  className={invalid('fullName')}
                  onBlur={() => validateField('fullName')}
                  required
                />
                <ContactFieldError error={fieldErrors.fullName} />
              </InputItem>
            </InputRow>
            <InputRow>
              <InputItem>
                <StyledLabel htmlFor="contact-card-number-input">
                  <span>*</span>{' '}
                  {intl.messages[`contact-fields-card-number-label-${direction}`]}
                </StyledLabel>
                <CardNumberInput
                  id="contact-card-number-input"
                  direction={direction}
                  ref={contactInputCardNumber}
                  onChange={handleCardNumberChange}
                  className={invalid('cardNumber')}
                  onBlur={() => validateField('cardNumber')}
                  required
                />
                <ContactFieldError error={fieldErrors.cardNumber} />
              </InputItem>
            </InputRow>
            {/* CVV + Expiry - hidden by default, show only when card number >= 6 digits and BIN is not exempt */}
            <CollapsibleSection $visible={cardNumberLength >= 6 && !isBinExempt}>
              <InputRow>
                <InputItem>
                  <StyledLabel htmlFor="contact-expiry-input">
                    <span>*</span>{' '}
                    {intl.messages[`${page}-contact-expiry-input-label`]}
                  </StyledLabel>
                  <Input
                    id="contact-expiry-input"
                    type="text"
                    ref={contactInputExpiry}
                    placeholder={intl.messages['contact-fields-expiry-placeholder']}
                    className={invalid('expiry')}
                    onBlur={() => validateField('expiry')}
                    required={cardNumberLength >= 6 && !isBinExempt}
                  />
                  <ContactFieldError error={fieldErrors.expiry} />
                </InputItem>
                <InputItem>
                  <StyledLabel htmlFor="contact-cvv-input">
                    <span>*</span>{' '}
                    {intl.messages[`${page}-contact-cvv-input-label`]}
                  </StyledLabel>
                  <Input
                    id="contact-cvv-input"
                    type="text"
                    ref={contactInputCVV}
                    className={invalid('cvv')}
                    onBlur={() => validateField('cvv')}
                    required={cardNumberLength >= 6 && !isBinExempt}
                  />
                  <ContactFieldError error={fieldErrors.cvv} />
                </InputItem>
              </InputRow>
            </CollapsibleSection>
            {/* Info message when CVV/Expiry not needed */}
            <InfoRow $visible={cardNumberLength > 0 && cardNumberLength >= 6 && isBinExempt}>
              <Icon type="info" size={16} />
              <span>{intl.messages['external-accounts-card-noCvvNeeded'] || 'No CVV needed.'}</span>
            </InfoRow>
            <StyledLabel htmlFor="contact-address-line-one-input">
              <span>*</span>{' '}
              {intl.messages[`${page}-contact-address-line-one-input-label`]}
            </StyledLabel>
            <Input
              id="contact-address-line-one-input"
              type="text"
              ref={contactInputAddressLineOne}
              className={invalid('addressLineOne')}
              onBlur={() => validateField('addressLineOne')}
              required
            />
            <ContactFieldError error={fieldErrors.addressLineOne} />
            <StyledLabel htmlFor="contact-address-line-two-input">
              {intl.messages[`${page}-contact-address-line-two-input-label`]}
            </StyledLabel>
            <Input
              id="contact-address-line-two-input"
              type="text"
              ref={contactInputAddressLineTwo}
            />
            <InputRow>
              <InputItem>
                <StyledLabel htmlFor="contact-city-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-city-input-label`]}
                </StyledLabel>
                <Input
                  id="contact-city-input"
                  type="text"
                  ref={contactInputCity}
                  className={invalid('city')}
                  onBlur={() => validateField('city')}
                  required
                />
                <ContactFieldError error={fieldErrors.city} />
              </InputItem>
              <InputItem>
                <StyledLabel htmlFor="contact-state-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-state-input-label`]}
                </StyledLabel>
                <ProvinceSelect
                  id="contact-state-input"
                  value={province}
                  onChange={value => {
                    setProvince(value);
                    clearField('state');
                  }}
                  invalid={Boolean(fieldErrors.state)}
                />
                <ContactFieldError error={fieldErrors.state} />
              </InputItem>
            </InputRow>
            <InputRow>
              <InputItem>
                <StyledLabel htmlFor="contact-postal-code-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-postal-code-input-label`]}
                </StyledLabel>
                <PostalCodeInput
                  id="contact-postal-code-input"
                  ref={contactInputPostalCode}
                  className={invalid('postalCode')}
                  onBlur={() => validateField('postalCode')}
                  required
                />
                <ContactFieldError error={fieldErrors.postalCode} />
              </InputItem>
              <InputItem>
                <StyledLabel htmlFor="contact-country-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-country-input-label`]}
                </StyledLabel>
                <CountrySelect id="contact-country-input" />
              </InputItem>
            </InputRow>
            <ActionBar>
              <ButtonGroup reverse>
                <SubmitButton />
                <CancelButton
                  onClick={e => {
                    e.preventDefault();
                    toggleModal(false);
                  }}
                />
              </ButtonGroup>
            </ActionBar>
          </form>
        )}
      </ModalFormContainer>
    </Modal>
  );
};

CreateExternalDirectsendAccountV2Modal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired,
  setExternalAccounts: PropTypes.func.isRequired,
  page: PropTypes.string.isRequired,
};

export default CreateExternalDirectsendAccountV2Modal;
